/**
 * Chat Store - Pinia store for chat state management
 * Follows the established store patterns in the project
 */

import { defineStore } from 'pinia'
import { socketService } from '@/services/socketService'
import type { 
  ChatState,
  Conversation
} from '@/types/chat'

export const useChatStore = defineStore('chat', {
  state: (): ChatState => ({
    // Socket connection
    isConnected: false,
    socket: null,
    
    // Conversations
    activeConversation: null,
    
    // UI state
    unreadCounts: {},
    
    // Organization status
    onlineOrganizations: new Set<string>(),
    
    // Typing indicators
    activeTypingUsers: {},
    
    // Error handling
    lastError: null as string | null,
    connectionRetryAttempts: 0,
    maxRetryAttempts: 3
  }),

  getters: {
    // Get total unread messages count across all conversations
    totalUnreadMessages: (state) => {
      return Object.values(state.unreadCounts).reduce((total, count) => total + count, 0)
    },

    // Get unread count for specific conversation
    getUnreadCount: (state) => (conversationId: string) => {
      return state.unreadCounts[conversationId] || 0
    },

    // Check if organization is online
    isOrganizationOnline: (state) => (orgProfileId: string) => {
      return state.onlineOrganizations.has(orgProfileId)
    },

    // Check if any user from an organization is typing
    isOrganizationTyping: () => () => {
      // This is a simplified implementation
      // In a full implementation, you'd track which users belong to which org
      // For now, we'll just return false as this is mainly for typing indicators
      return false
    },

    // Check if there's a current error
    hasError: (state) => state.lastError !== null,

  },

  actions: {
    /**
     * Initialize socket connection
     */
    async initializeSocket() {
      try {
        this.socket = await socketService.connect()
        this.isConnected = true
      } catch (error) {
        this.isConnected = false
        this.handleSocketError('Failed to connect to chat server. Please check your internet connection.')
        throw error
      }
    },

    /**
     * Initialize chat system globally (for app-wide usage)
     * This ensures socket connection and unread counts are available everywhere
     */
    async initializeGlobally() {
      // Only initialize socket if not already connected
      if (!this.isConnected) {
        try {
          await this.initializeSocket()
        } catch (error) {
          console.warn('Socket initialization failed during global chat setup:', error)
          // Continue without socket - unread counts can still be loaded
        }
      }
    },

    /**
     * Disconnect and reset chat state (useful for logout)
     */
    disconnect() {
      if (this.socket) {
        socketService.disconnect()
        this.socket = null
        this.isConnected = false
      }
      // Clear unread counts
      this.unreadCounts = {}
      this.activeConversation = null
      this.lastError = null
      this.connectionRetryAttempts = 0
    },

    /**
     * Set active conversation
     */
    setActiveConversation(conversation: Conversation | null) {
      this.activeConversation = conversation
    },

    /**
     * Start typing to a chat partner
     */
    startTyping(chatPartnerId: string | number) {
      if (this.isConnected) {
        socketService.startTyping(chatPartnerId)
      }
    },

    /**
     * Stop typing to a chat partner
     */
    stopTyping(chatPartnerId: string | number) {
      if (this.isConnected) {
        socketService.stopTyping(chatPartnerId)
      }
    },

    /**
     * Handle socket connection errors
     */
    handleSocketError(message: string) {
      this.lastError = message
      this.isConnected = false
      
      setTimeout(() => {
        this.lastError = null
      }, 10000)
    },

    /**
     * Clear error state
     */
    clearError() {
      this.lastError = null
    },

    /**
     * Update unread count for a conversation
     */
    setUnreadCount(conversationId: string, count: number) {
      if (count <= 0) {
        delete this.unreadCounts[conversationId]
      } else {
        this.unreadCounts[conversationId] = count
      }
    },

    /**
     * Increment unread count for a conversation
     */
    incrementUnreadCount(conversationId: string, increment = 1) {
      const currentCount = this.unreadCounts[conversationId] || 0
      this.setUnreadCount(conversationId, currentCount + increment)
    },

    /**
     * Mark conversation as read (reset unread count to 0)
     */
    markConversationAsRead(conversationId: string) {
      this.setUnreadCount(conversationId, 0)
    },

    /**
     * Initialize unread counts from conversations data
     */
    initializeUnreadCounts(conversations: Array<{ documentId: string; unread_count?: number }>) {
      this.unreadCounts = {}
      conversations.forEach(conversation => {
        if (conversation.unread_count && conversation.unread_count > 0) {
          this.unreadCounts[conversation.documentId] = conversation.unread_count
        }
      })
    },

  }
})